// Display-only strings for the public Religious Education registration form.
// Values submitted to the API (radio values, relationship keys, sacrament keys,
// payment method keys) stay in English elsewhere so the backend and management
// page are unaffected by the user's language choice.

const en = {
  languageLabel: { en: 'English', es: 'Español' },

  // Page-level
  header: 'Religious Education Registration',
  loading: 'Loading registration form...',
  closedTitle: 'Registration Closed',
  closedBody:
    'Sorry, registration for Religious Education is currently closed. Please contact our parish office for more information.',
  successTitle: 'Registration Submitted!',
  successBody:
    'Thank you for registering for Religious Education at St. Bridget. You will receive a confirmation email shortly.',
  successCashReminder: 'Remember to submit your payment to the parish office.',
  genericSubmitError: 'Failed to submit registration.',
  paymentFailed: 'Payment failed. Please check your card details.',

  // Stepper
  steps: ['Welcome', 'Parish Registration', 'Family Information', 'Children', 'Permissions', 'Signature', 'Payment'],
  stepOfTotal: (current, total) => `Step ${current} of ${total}`,

  // Common buttons / labels
  yes: 'Yes',
  no: 'No',
  back: 'Back',
  next: 'Next',
  submit: 'Submit Registration',
  remove: 'Remove',
  required: 'required',

  // Welcome step
  welcomeTitle: 'Welcome!',
  returningQuestion: 'Have you registered for Religious Education at St. Bridget before?',
  returningYes: "Yes, I'm a returning family",
  returningNo: 'No, this is my first time',
  returningEmailBlurb:
    "Enter your email address and we'll send you a verification code to pre-fill your information.",
  emailAddress: 'Email Address',
  sendVerificationCode: 'Send Verification Code',
  verificationCode: 'Verification Code',
  verifyAndPrefill: 'Verify & Pre-fill',
  skipAndStartFresh: 'Skip and start fresh',
  pleaseEnterEmail: 'Please enter your email address.',
  codeSent: 'Verification code sent! Check your email.',
  codeSendFailed: 'Failed to send verification code.',
  pleaseEnterCode: 'Please enter the verification code.',
  infoLoaded: 'Information loaded! Review and continue.',
  invalidCode: 'Invalid or expired code.',

  // Parish step
  parishStepTitle: 'Parish Registration',
  parishQuestion: 'Is your family currently registered at St. Bridget University Parish?',
  parishNotRegisteredBefore: 'In order to register for Religious Education, your family must be registered at our Parish. Please ',
  parishNotRegisteredLink: 'fill out our parish registration form',
  parishNotRegisteredAfter: ' first.',

  // Family info step
  familyStepTitle: 'Family Information',
  parentsGuardians: 'Parents / Guardians',
  parentGuardian1: 'Parent/Guardian 1',
  parentGuardian2: 'Parent/Guardian 2',
  fullName: 'Full Name',
  relationship: 'Relationship',
  relationshipOptions: {
    Father: 'Father',
    Mother: 'Mother',
    Grandparent: 'Grandparent',
    Guardian: 'Guardian',
    Other: 'Other'
  },
  maidenName: 'Maiden Name (optional)',
  ifApplicable: 'If applicable',
  addSecondParent: '+ Add Second Parent/Guardian',
  primaryContact: 'Primary Contact for Religious Education',
  primaryContactBlurb: "This is who we will contact regarding your child's religious education.",
  contactName: 'Contact Name',
  phone: 'Phone',
  email: 'Email',
  invalidEmailHelper: 'Please enter a valid email address (e.g., name@example.com).',

  // Children step
  childInformationSuffix: "'s Information",
  newChild: 'New Child',
  childN: (n) => `Child #${n}`,
  removeThisChild: 'Remove This Child',
  childsName: "Child's Name",
  dateOfBirth: 'Date of Birth',
  grade: 'Grade',
  gradeWithYear: (year) => `Grade (${year})`,
  lastYearGrade: (g) => `Last year: Grade ${g}`,
  catholicSchoolQuestion: 'Does your child attend a Catholic School?',
  schoolName: 'School Name',
  sacramentsReceivedQuestion: 'Has your child received any of the following sacraments?',
  sacramentsPreparingQuestion: 'Is your child preparing to receive any sacraments?',
  sacramentLabels: {
    baptism: 'Baptism',
    communion: 'Communion',
    confirmation: 'Confirmation'
  },
  continuingSacPrepTitle: 'Continuing Sacramental Preparation',
  continuingSacPrepBody: (name, sacramentList) =>
    `We see that ${name} was preparing for ${sacramentList} last year. Sacramental preparation is a two-year program.`,
  yourChild: 'your child',
  sacramentJoiner: ' and ',
  paidSacramentFeeQuestion: 'Did you already pay the sacramental preparation fee last year?',
  yesAlreadyPaid: 'Yes, I already paid',
  noDidNotPay: 'No, I did not pay',
  baptizedAtStBridgetQuestion: 'Was your child baptized at St. Bridget?',
  previouslyEnrolledQuestion: 'Was your child previously enrolled in Religious Education?',
  uploadCertificatePrompt: "Please upload a copy of your child's baptismal certificate:",
  chooseFile: 'Choose File',
  certificateAcknowledgment:
    "If I do not submit my child's baptismal certificate now, I acknowledge that St. Bridget University Parish already has our child's Baptismal Certificate, or I will provide it to St. Bridget University Parish immediately.",
  programSelection: 'Program Selection',
  willBeEnrolledInPrefix: 'Your child will be enrolled in: ',
  invalidChildrenHeader: 'These children still need information before you can continue:',
  addAnotherChild: '+ Add Another Child',
  doneAddingChildren: 'Done Adding Children',
  previousChild: (name) => `Previous: ${name}`,
  nextChild: (name) => `Next: ${name}`,
  validationIssues: {
    name: 'Name',
    dateOfBirth: 'Date of Birth',
    grade: 'Grade',
    catholicSchool: 'Catholic School question',
    program: 'Program',
    certificate: 'Baptismal certificate (upload or acknowledgment)'
  },

  // Permissions step
  permissionsStepTitle: 'Permissions',
  photoPermissionLabel: 'Permission to Photograph',
  photoPermissionBody:
    'I grant St. Bridget University Parish the right to take photographs of my child and our family during faith formation activities and parish events. I authorize St. Bridget University Parish to publish the same in print and/or electronically.',
  grantPermission: 'Yes, I grant permission',
  denyPermission: 'No, I do not grant permission',

  // Signature step
  signatureStepTitle: 'Signature',
  parentGuardianSignature: 'Parent/Guardian Signature',
  typeYourFullName: 'Type your full name',
  date: 'Date',

  // Payment step
  paymentStepTitle: 'Payment Information',
  registrationSummary: 'Registration Summary',
  totalDue: 'Total Due:',
  freeFormationNote: 'No one will be deprived of faith formation based on the inability to pay.',
  paymentMethod: 'Payment Method',
  payOnline: 'Pay Online',
  payInPerson: 'Pay in Person',
  paymentAmount: 'Payment Amount',
  paymentAmountHelper: (total) => `Minimum $20, Maximum $${total.toFixed(2)}`,
  cashInstructions:
    'You may place your payment in the collection basket in a marked envelope, drop it off at the parish office or mail it in. Please make checks payable to St. Bridget Church. Please do not send cash in mail.',
  free: 'FREE',

  // Payment breakdown
  attendsCatholicSchool: (name) => `${name} - Attends Catholic School`,
  noFeeRequired: 'No fee required',
  registrationFeeLine: (name) => `${name} - Registration Fee`,
  additionalChildFeeLine: (name) => `${name} - Additional Child Fee`,
  earlyDiscountApplied: 'Early registration discount applied',
  sacramentPrepLine: (name, sacrament) => `${name} - ${sacrament} Preparation`,
  year2PreviouslyPaid: 'Year 2 - Previously paid',
  year2Outstanding: 'Year 2 - Outstanding from previous year',
  childPlaceholder: (n) => `Child ${n}`
};

const es = {
  languageLabel: { en: 'English', es: 'Español' },

  // Page-level
  header: 'Inscripción para Educación Religiosa',
  loading: 'Cargando el formulario de inscripción...',
  closedTitle: 'Inscripciones cerradas',
  closedBody:
    'Lo sentimos, las inscripciones para Educación Religiosa están cerradas en este momento. Por favor comuníquese con la oficina parroquial para más información.',
  successTitle: '¡Inscripción enviada!',
  successBody:
    'Gracias por inscribirse en Educación Religiosa en St. Bridget. Recibirá un correo electrónico de confirmación en breve.',
  successCashReminder: 'Recuerde entregar su pago en la oficina parroquial.',
  genericSubmitError: 'No se pudo enviar la inscripción.',
  paymentFailed: 'Pago fallido. Por favor verifique los datos de su tarjeta.',

  // Stepper
  steps: ['Bienvenida', 'Inscripción Parroquial', 'Información Familiar', 'Hijos', 'Permisos', 'Firma', 'Pago'],
  stepOfTotal: (current, total) => `Paso ${current} de ${total}`,

  // Common buttons / labels
  yes: 'Sí',
  no: 'No',
  back: 'Atrás',
  next: 'Siguiente',
  submit: 'Enviar Inscripción',
  remove: 'Eliminar',
  required: 'obligatorio',

  // Welcome step
  welcomeTitle: '¡Bienvenido!',
  returningQuestion: '¿Se ha inscrito antes en Educación Religiosa en St. Bridget?',
  returningYes: 'Sí, somos una familia que regresa',
  returningNo: 'No, es nuestra primera vez',
  returningEmailBlurb:
    'Ingrese su correo electrónico y le enviaremos un código de verificación para llenar su información automáticamente.',
  emailAddress: 'Correo electrónico',
  sendVerificationCode: 'Enviar código de verificación',
  verificationCode: 'Código de verificación',
  verifyAndPrefill: 'Verificar y llenar',
  skipAndStartFresh: 'Omitir y empezar de nuevo',
  pleaseEnterEmail: 'Por favor ingrese su correo electrónico.',
  codeSent: '¡Código de verificación enviado! Revise su correo electrónico.',
  codeSendFailed: 'No se pudo enviar el código de verificación.',
  pleaseEnterCode: 'Por favor ingrese el código de verificación.',
  infoLoaded: '¡Información cargada! Revise y continúe.',
  invalidCode: 'Código inválido o expirado.',

  // Parish step
  parishStepTitle: 'Inscripción Parroquial',
  parishQuestion: '¿Su familia está actualmente inscrita en la Parroquia Universitaria St. Bridget?',
  parishNotRegisteredBefore: 'Para inscribirse en Educación Religiosa, su familia debe estar inscrita en nuestra Parroquia. Por favor ',
  parishNotRegisteredLink: 'complete nuestro formulario de inscripción parroquial',
  parishNotRegisteredAfter: ' primero.',

  // Family info step
  familyStepTitle: 'Información Familiar',
  parentsGuardians: 'Padres / Tutores',
  parentGuardian1: 'Padre/Tutor 1',
  parentGuardian2: 'Padre/Tutor 2',
  fullName: 'Nombre completo',
  relationship: 'Relación',
  relationshipOptions: {
    Father: 'Padre',
    Mother: 'Madre',
    Grandparent: 'Abuelo/Abuela',
    Guardian: 'Tutor',
    Other: 'Otro'
  },
  maidenName: 'Apellido de soltera (opcional)',
  ifApplicable: 'Si corresponde',
  addSecondParent: '+ Agregar segundo padre/tutor',
  primaryContact: 'Contacto principal para Educación Religiosa',
  primaryContactBlurb: 'Esta es la persona a quien contactaremos sobre la educación religiosa de su hijo/a.',
  contactName: 'Nombre del contacto',
  phone: 'Teléfono',
  email: 'Correo electrónico',
  invalidEmailHelper: 'Por favor ingrese un correo electrónico válido (ej., nombre@ejemplo.com).',

  // Children step
  childInformationSuffix: ' - Información',
  newChild: 'Nuevo hijo/a',
  childN: (n) => `Hijo/a #${n}`,
  removeThisChild: 'Eliminar este hijo/a',
  childsName: 'Nombre del hijo/a',
  dateOfBirth: 'Fecha de nacimiento',
  grade: 'Grado',
  gradeWithYear: (year) => `Grado (${year})`,
  lastYearGrade: (g) => `Año pasado: Grado ${g}`,
  catholicSchoolQuestion: '¿Asiste su hijo/a a una escuela católica?',
  schoolName: 'Nombre de la escuela',
  sacramentsReceivedQuestion: '¿Ha recibido su hijo/a alguno de los siguientes sacramentos?',
  sacramentsPreparingQuestion: '¿Su hijo/a se está preparando para recibir algún sacramento?',
  sacramentLabels: {
    baptism: 'Bautismo',
    communion: 'Comunión',
    confirmation: 'Confirmación'
  },
  continuingSacPrepTitle: 'Preparación sacramental en curso',
  continuingSacPrepBody: (name, sacramentList) =>
    `Vemos que ${name} se estaba preparando para ${sacramentList} el año pasado. La preparación sacramental es un programa de dos años.`,
  yourChild: 'su hijo/a',
  sacramentJoiner: ' y ',
  paidSacramentFeeQuestion: '¿Ya pagó la cuota de preparación sacramental el año pasado?',
  yesAlreadyPaid: 'Sí, ya pagué',
  noDidNotPay: 'No, no pagué',
  baptizedAtStBridgetQuestion: '¿Fue su hijo/a bautizado/a en St. Bridget?',
  previouslyEnrolledQuestion: '¿Estuvo su hijo/a inscrito/a anteriormente en Educación Religiosa?',
  uploadCertificatePrompt: 'Por favor suba una copia del certificado de bautismo de su hijo/a:',
  chooseFile: 'Elegir archivo',
  certificateAcknowledgment:
    'Si no envío ahora el certificado de bautismo de mi hijo/a, reconozco que la Parroquia Universitaria St. Bridget ya tiene el Certificado de Bautismo de nuestro hijo/a, o lo entregaré a la Parroquia Universitaria St. Bridget de inmediato.',
  programSelection: 'Selección de programa',
  willBeEnrolledInPrefix: 'Su hijo/a será inscrito/a en: ',
  invalidChildrenHeader: 'Estos hijos/as necesitan información antes de poder continuar:',
  addAnotherChild: '+ Agregar otro hijo/a',
  doneAddingChildren: 'Terminar de agregar hijos/as',
  previousChild: (name) => `Anterior: ${name}`,
  nextChild: (name) => `Siguiente: ${name}`,
  validationIssues: {
    name: 'Nombre',
    dateOfBirth: 'Fecha de nacimiento',
    grade: 'Grado',
    catholicSchool: 'Pregunta sobre escuela católica',
    program: 'Programa',
    certificate: 'Certificado de bautismo (subir o reconocimiento)'
  },

  // Permissions step
  permissionsStepTitle: 'Permisos',
  photoPermissionLabel: 'Permiso para fotografiar',
  photoPermissionBody:
    'Otorgo a la Parroquia Universitaria St. Bridget el derecho de tomar fotografías de mi hijo/a y de nuestra familia durante las actividades de formación en la fe y los eventos parroquiales. Autorizo a la Parroquia Universitaria St. Bridget a publicar las mismas en forma impresa y/o electrónica.',
  grantPermission: 'Sí, otorgo permiso',
  denyPermission: 'No, no otorgo permiso',

  // Signature step
  signatureStepTitle: 'Firma',
  parentGuardianSignature: 'Firma del padre/tutor',
  typeYourFullName: 'Escriba su nombre completo',
  date: 'Fecha',

  // Payment step
  paymentStepTitle: 'Información de pago',
  registrationSummary: 'Resumen de la inscripción',
  totalDue: 'Total a pagar:',
  freeFormationNote: 'Nadie será privado de la formación en la fe por no poder pagar.',
  paymentMethod: 'Método de pago',
  payOnline: 'Pagar en línea',
  payInPerson: 'Pagar en persona',
  paymentAmount: 'Monto del pago',
  paymentAmountHelper: (total) => `Mínimo $20, Máximo $${total.toFixed(2)}`,
  cashInstructions:
    'Puede colocar su pago en la canasta de la colecta en un sobre marcado, dejarlo en la oficina parroquial o enviarlo por correo. Los cheques deben hacerse a nombre de St. Bridget Church. Por favor no envíe efectivo por correo.',
  free: 'GRATIS',

  // Payment breakdown
  attendsCatholicSchool: (name) => `${name} - Asiste a escuela católica`,
  noFeeRequired: 'No se requiere cuota',
  registrationFeeLine: (name) => `${name} - Cuota de inscripción`,
  additionalChildFeeLine: (name) => `${name} - Cuota por hijo/a adicional`,
  earlyDiscountApplied: 'Descuento por inscripción anticipada aplicado',
  sacramentPrepLine: (name, sacrament) => `${name} - Preparación para ${sacrament}`,
  year2PreviouslyPaid: 'Año 2 - Pagado anteriormente',
  year2Outstanding: 'Año 2 - Pendiente del año anterior',
  childPlaceholder: (n) => `Hijo/a ${n}`
};

export const translations = { en, es };
